import { useEffect, useState } from 'react';

import type { CountryCode } from '@hotjar/country-lib';
import type { PaymentDetailsConfig, ZuoraCreditCardDetailsOptions } from '../types';
import { mapZuoraCreditCardDetailsDTOtoModel } from '../mapper';
import { api } from '../api/configurations';

interface ParametersOptions {
  countryCode?: CountryCode;
  customerId?: number;
}

export const getZuoraParameters = async ({
  countryCode,
  customerId,
}: ParametersOptions): Promise<ZuoraCreditCardDetailsOptions> => {
  /**
   * there are two different ways to retrieve configurations:
   * 1) with country code - a fallback when no customer exists yet, and;
   * 2) with customer id - when customer is found
   */
  const getConfigs = () =>
    customerId
      ? api.getConfigurationsFromCustomerId(customerId)
      : api.getConfigurationsFromCountryCode(countryCode as CountryCode);

  const configs: PaymentDetailsConfig[] = await getConfigs();
  const parameters = configs.find(
    ({ paymentMethodType, provider }) =>
      provider === 'zuora' && paymentMethodType === 'credit_card',
  );
  return mapZuoraCreditCardDetailsDTOtoModel((parameters as PaymentDetailsConfig)?.options);
};

export const useZuoraParameters = ({
  customerId,
  countryCode,
}: ParametersOptions): ZuoraCreditCardDetailsOptions | Error | undefined => {
  const [parameters, setParameters] = useState<ZuoraCreditCardDetailsOptions | Error>();

  useEffect(() => {
    getZuoraParameters({ customerId, countryCode })
      .then(setParameters)
      .catch(() => {
        setParameters(
          new Error(
            `Could not load credit card payment method form for ${
              customerId ? `customer account id: ${customerId}` : `country code: ${countryCode}`
            }`,
          ),
        );
      });
  }, [customerId, countryCode]);

  return parameters;
};
